import React, { useEffect, useState } from 'react';
import { Insight } from '../types/insights';
import { safeUpperCase, safeIncludes, safeArray, safeString, safeLowerCase } from '../utils/safeString';

interface InsightCardProps {
  insight: Insight;
  isExpanded: boolean;
  onToggle: () => void;
  onClose?: () => void;
}

const InsightCard: React.FC<InsightCardProps> = ({ insight, isExpanded, onToggle, onClose }) => {
  const followUpQuestions = safeArray(insight.follow_up_questions);

  const normalizeFollowUpQuestion = (question?: string): string => {
    const raw = safeString(question, '').trim();
    if (!raw) return '';

    const withoutLabel = raw
      .replace(/^ask\s*coworker\s*[-–—:]?\s*/i, '')
      .replace(/^ask\s*coworker/i, '')
      .trim();

    const withoutQuotes = withoutLabel
      .replace(/^["'“”]+/, '')
      .replace(/["'“”]+$/, '')
      .trim();

    return withoutQuotes || withoutLabel || raw;
  };

  const getPriorityTagClass = (tag?: string): string => {
    const upperTag = safeUpperCase(tag);
    switch (upperTag) {
      case 'COACHING':
        return 'priority-coaching';
      case 'HIGH RISK':
        return 'priority-high-risk';
      case 'QUICK WIN':
        return 'priority-quick-win';
      case 'WORKFLOW':
        return 'priority-workflow';
      case 'OPPORTUNITY':
      default:
        return 'priority-opportunity';
    }
  };

  const getActionButtons = () => {
    const actions = safeArray(insight.suggested_actions);
    const hasCreateAction = actions.some(a => safeString(a?.action_type) === 'Create');
    const hasDiscussAction = actions.some(a => safeString(a?.action_type) === 'Discuss');
    const hasScheduleAction = actions.some(a => safeString(a?.action_type) === 'Schedule');
    const hasReviewAction = actions.some(a => safeString(a?.action_type) === 'Review');

    const buttons = [];
    
    if (hasCreateAction || safeIncludes(insight.category, 'EM Performance')) {
      buttons.push(
        <button key="prep" className="action-btn primary">
          Prep with Go2
        </button>
      );
      buttons.push(
        <button key="resources" className="action-btn secondary">
          View Resources
        </button>
      );
    } else if (hasDiscussAction) {
      buttons.push(
        <button key="plan" className="action-btn primary">
          Plan Discussion
        </button>
      );
      buttons.push(
        <button key="history" className="action-btn secondary">
          View History
        </button>
      );
    } else if (hasScheduleAction || hasReviewAction) {
      buttons.push(
        <button key="review" className="action-btn primary">
          Review Focus
        </button>
      );
      buttons.push(
        <button key="discuss" className="action-btn secondary">
          Discuss Further
        </button>
      );
    } else {
      buttons.push(
        <button key="draft" className="action-btn primary">
          Draft Announcement
        </button>
      );
      buttons.push(
        <button key="find" className="action-btn secondary">
          Find Link
        </button>
      );
    }
    
    return buttons;
  };

  const normalizedFollowUps = Array.from(
    new Set(
      followUpQuestions
        .map(normalizeFollowUpQuestion)
        .filter(Boolean)
    )
  );

  const initialChatValue = normalizedFollowUps[0] || '';
  const [chatInput, setChatInput] = useState(initialChatValue);

  useEffect(() => {
    if (!chatInput && initialChatValue) {
      setChatInput(initialChatValue);
    }
  }, [chatInput, initialChatValue]);

  return (
    <div className={`insight-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="insight-header" onClick={onToggle}>
        <div className="category-info">
          <span className="category-icon">{safeString(insight.category_icon, '📊')}</span>
          <span className="category-name">{safeString(insight.category, 'Unknown')}</span>
          <span className={`priority-tag ${getPriorityTagClass(insight.priority_tag)}`}>
            {safeString(insight.priority_tag, 'opportunity')}
          </span>
          {insight.insight_mode && (
            <span className={`insight-mode insight-mode-${safeLowerCase(insight.insight_mode)}`}>
              {safeString(insight.insight_mode, '').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          )}
        </div>
        <div className="card-actions">
          {isExpanded && (
            <>
              <button className="icon-btn" title="Star">⭐</button>
              <button className="icon-btn" title="Share">🔗</button>
              <button
                className="icon-btn"
                title="Close"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose?.();
                }}
                type="button"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="insight-content">
          <div className="main-insight">
            {safeString(insight.main_insight, 'No insight available')}
          </div>
          
          <div className="why-it-matters">
            <em>{safeString(insight.why_it_matters, 'Impact not specified')}</em>
          </div>
          
          <div className="recommendation">
            {safeString(insight.recommendation, 'No recommendation available')}
          </div>
          
          <div className="action-buttons">
            {getActionButtons()}
          </div>

          <div className="chat-section">
            <input
              type="text"
              placeholder="Start a new chat to discuss or question this insight..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              className="chat-input"
            />
            <div className="chat-actions">
              <button className="icon-btn" title="Help">?</button>
              <button className="icon-btn" title="Send">➤</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightCard;
